import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import {
  BriefcaseBusiness,
  MapPin,
  Users,
  Clock3,
  RefreshCw,
  Eye,
  Pencil,
  Trash2,
  AlertCircle,
  Building2,
  X,
  Save,
  CalendarDays,
  IndianRupee,
  FileText,
  Search,
  SlidersHorizontal,
  RotateCcw,
  ArrowUpDown,
  CheckCircle2,
  XCircle,
  UserCheck,
   ChevronRight,
} from "lucide-react";

import Swal from "sweetalert2";
import jobService from "../../services/JobService";
import PartnerFooter from "../../component/PartnerFooter";
import PartnerHeader from "../../component/PartnerHeader";

/* =========================================================
   CONSTANTS
========================================================= */

const API_BASE = "/api";

const COLORS = {
  primary: "#3E0F8D",
  secondary: "#9564DD",
  accent: "#E4DA72",
  light: "#EEEEEE",
};

const STATUS_OPTIONS = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "BLOCKED",
  "INACTIVE",
];

const EMPTY_EDIT_JOB = {
  jobId: "",
  jobTitle: "",
  jobDescription: "",
  location: "",
  employeeType: "",
  experienceRequired: "",
  salaryMin: "",
  salaryMax: "",
  skillsRequired: "",
  vacancy: "",
  createdAt: "",
  applicationDeadline: "",
  status: "PENDING",
  applicationCount: 0,
  postedBy: null,
};

/* =========================================================
   HELPERS
========================================================= */

const firstValue = (...values) => {
  for (const value of values) {
    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return null;
};

const getJobId = (job) =>
  firstValue(
    job?.jobId,
    job?.job_id,
    job?.id
  );

const getPostedById = (job) => {
  const postedBy = firstValue(
    job?.postedBy,
    job?.posted_by,
    job?.postedById,
    job?.posted_by_id
  );

  if (
    postedBy &&
    typeof postedBy === "object"
  ) {
    return firstValue(
      postedBy.id,
      postedBy.userId,
      postedBy.partnerId
    );
  }

  return postedBy;
};

const getJobTitle = (job) =>
  firstValue(
    job?.jobTitle,
    job?.job_title
  ) || "";

const getJobDescription = (job) =>
  firstValue(
    job?.jobDescription,
    job?.job_description
  ) || "";

const getLocation = (job) =>
  firstValue(
    job?.location
  ) || "";

const getEmployeeType = (job) =>
  firstValue(
    job?.employeeType,
    job?.employee_type
  ) || "";

const getExperienceRequired = (job) =>
  firstValue(
    job?.experienceRequired,
    job?.experience_required
  ) || "";

const getSalaryMin = (job) =>
  firstValue(
    job?.salaryMin,
    job?.salary_min
  );

const getSalaryMax = (job) =>
  firstValue(
    job?.salaryMax,
    job?.salary_max
  );

const getSkillsRequired = (job) =>
  firstValue(
    job?.skillsRequired,
    job?.skills_required
  ) || "";

const getVacancy = (job) =>
  firstValue(
    job?.vacancy
  );

const getCreatedAt = (job) =>
  firstValue(
    job?.createdAt,
    job?.created_at
  );

const getApplicationDeadline = (job) =>
  firstValue(
    job?.applicationDeadline,
    job?.application_deadline
  );

const getStatus = (job) =>
  firstValue(
    job?.status
  ) || "PENDING";

const getApplicationCount = (job) =>
  firstValue(
    job?.applicationCount,
    job?.application_count,
    job?.applicationsCount,
    job?.applications
  ) ?? 0;

/* =========================================================
   NORMALIZE JOB
========================================================= */

const normalizeJob = (job) => ({
  ...job,

  jobId: getJobId(job),
  postedBy: job?.postedBy ?? job?.posted_by ?? null,
  postedById: getPostedById(job),

  jobTitle: getJobTitle(job),
  jobDescription: getJobDescription(job),

  location: getLocation(job),
  employeeType: getEmployeeType(job),
  experienceRequired: getExperienceRequired(job),

  salaryMin: getSalaryMin(job),
  salaryMax: getSalaryMax(job),

  skillsRequired: getSkillsRequired(job),

  vacancy: getVacancy(job),

  createdAt: getCreatedAt(job),
  applicationDeadline:
    getApplicationDeadline(job),

  status: getStatus(job),

  applicationCount:
    getApplicationCount(job),
});

/* =========================================================
   RESPONSE ARRAY
========================================================= */

const normalizeJobsResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (
    Array.isArray(
      response?.data?.content
    )
  ) {
    return response.data.content;
  }

  return [];
};

/* =========================================================
   DATE HELPERS
========================================================= */

const formatDisplayDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  const hours = String(
    date.getHours()
  ).padStart(2, "0");

  const minutes = String(
    date.getMinutes()
  ).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/* =========================================================
   SALARY
========================================================= */

const formatSalary = (min, max) => {
  const hasMin =
    min !== null &&
    min !== undefined &&
    min !== "";

  const hasMax =
    max !== null &&
    max !== undefined &&
    max !== "";

  if (!hasMin && !hasMax) {
    return "Not specified";
  }

  const format = (value) =>
    Number(value).toLocaleString(
      "en-IN"
    );

  if (hasMin && hasMax) {
    return `₹${format(min)} - ₹${format(max)}`;
  }

  if (hasMin) {
    return `₹${format(min)}+`;
  }

  return `Up to ₹${format(max)}`;
};

/* =========================================================
   EXPERIENCE FILTER
========================================================= */

const experienceMatches = (
  experience,
  filter
) => {
  if (filter === "ALL") return true;

  const text = String(
    experience || ""
  ).toLowerCase();

  const numbers =
    text.match(/\d+/g)?.map(Number) || [];

  if (!numbers.length) {
    return false;
  }

  const min = Math.min(...numbers);
  const max = Math.max(...numbers);

  switch (filter) {
    case "0-2":
      return min <= 2;

    case "3-5":
      return (
        min <= 5 &&
        max >= 3
      );

    case "6-10":
      return (
        min <= 10 &&
        max >= 6
      );

    case "10+":
      return max >= 10;

    default:
      return true;
  }
};

/* =========================================================
   PAGE
========================================================= */

function MyJobs() {
  /* =======================================================
     STATE
  ======================================================= */
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [updatingStatus, setUpdatingStatus] =
    useState(null);

  /* FILTERS */

  const [filters, setFilters] =
    useState({
      search: "",
      status: "ALL",
      location: "ALL",
      employment: "ALL",
      experience: "ALL",
      vacancy: "ALL",
      applications: "ALL",
    });

  const [sortBy, setSortBy] =
    useState("newest");

  const [showFilters, setShowFilters] =
    useState(true);

  /* VIEW DRAWER */

  const [selectedJob, setSelectedJob] =
    useState(null);

  /* EDIT MODAL */

  const [editingJob, setEditingJob] =
    useState(null);

  const [savingJob, setSavingJob] =
    useState(false);

  /* =======================================================
     GET LOGGED IN PARTNER
  ======================================================= */

  const getLoggedInPartnerId = () => {
    const storedUser =
      localStorage.getItem("user");

    if (!storedUser) {
      return null;
    }

    try {
      const user =
        JSON.parse(storedUser);

      return firstValue(
        user?.partnerId,
        user?.partner?.id,
        user?.userId,
        user?.id
      );
    } catch (err) {
      console.error(
        "USER PARSE ERROR:",
        err
      );

      return null;
    }
  };

  /* =======================================================
     FETCH JOBS
  ======================================================= */

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError("");

      const partnerId =
        getLoggedInPartnerId();

      if (!partnerId) {
        setJobs([]);

        setError(
          "Partner information not found. Please login again."
        );

        return;
      }

      const response =
        await jobService.getAllJobs();

      const allJobs =
        normalizeJobsResponse(
          response
        ).map(normalizeJob);

      const myJobs =
        allJobs.filter((job) => {
          const postedById =
            getPostedById(job);

          return (
            Number(postedById) ===
            Number(partnerId)
          );
        });

      setJobs(myJobs);
    } catch (err) {
      console.error(
        "GET MY JOBS ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load your jobs."
      );

      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {
    fetchJobs();
  }, []);

  /* =======================================================
     UNIQUE FILTER OPTIONS
  ======================================================= */

  const locationOptions =
    useMemo(() => {
      return [
        ...new Set(
          jobs
            .map((job) =>
              String(
                job.location || ""
              ).trim()
            )
            .filter(Boolean)
        ),
      ].sort();
    }, [jobs]);

  const employmentOptions =
    useMemo(() => {
      return [
        ...new Set(
          jobs
            .map((job) =>
              String(
                job.employeeType || ""
              ).trim()
            )
            .filter(Boolean)
        ),
      ].sort();
    }, [jobs]);

  /* =======================================================
     FILTER + SORT
  ======================================================= */

  const filteredJobs =
    useMemo(() => {
      const search =
        filters.search
          .trim()
          .toLowerCase();

      const result =
        jobs.filter((job) => {
          /* SEARCH */

          if (search) {
            const searchable = [
              job.jobTitle,
              job.jobId,
              job.jobDescription,
              job.location,
              job.employeeType,
              job.experienceRequired,
              job.skillsRequired,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

            if (
              !searchable.includes(search)
            ) {
              return false;
            }
          }

          /* STATUS */

          if (
            filters.status !== "ALL" &&
            String(job.status)
              .toUpperCase() !==
              filters.status
          ) {
            return false;
          }

          /* LOCATION */

          if (
            filters.location !==
              "ALL" &&
            job.location !==
              filters.location
          ) {
            return false;
          }

          /* EMPLOYMENT */

          if (
            filters.employment !==
              "ALL" &&
            job.employeeType !==
              filters.employment
          ) {
            return false;
          }

          /* EXPERIENCE */

          if (
            !experienceMatches(
              job.experienceRequired,
              filters.experience
            )
          ) {
            return false;
          }

          /* VACANCY */

          const vacancy =
            Number(job.vacancy || 0);

          if (
            filters.vacancy ===
              "1-5" &&
            !(
              vacancy >= 1 &&
              vacancy <= 5
            )
          ) {
            return false;
          }

          if (
            filters.vacancy ===
              "6-10" &&
            !(
              vacancy >= 6 &&
              vacancy <= 10
            )
          ) {
            return false;
          }

          if (
            filters.vacancy ===
              "11+" &&
            vacancy < 11
          ) {
            return false;
          }

          /* APPLICATIONS */

          const applications =
            Number(
              job.applicationCount || 0
            );

          if (
            filters.applications ===
              "0" &&
            applications !== 0
          ) {
            return false;
          }

          if (
            filters.applications ===
              "1-10" &&
            !(
              applications >= 1 &&
              applications <= 10
            )
          ) {
            return false;
          }

          if (
            filters.applications ===
              "11-50" &&
            !(
              applications >= 11 &&
              applications <= 50
            )
          ) {
            return false;
          }

          if (
            filters.applications ===
              "51+" &&
            applications < 51
          ) {
            return false;
          }

          return true;
        });

      /* SORT */

      result.sort((a, b) => {
        switch (sortBy) {
          case "oldest":
            return (
              new Date(
                a.createdAt || 0
              ) -
              new Date(
                b.createdAt || 0
              )
            );

          case "title":
            return String(
              a.jobTitle || ""
            ).localeCompare(
              String(
                b.jobTitle || ""
              )
            );

          case "vacancy-high":
            return (
              Number(b.vacancy || 0) -
              Number(a.vacancy || 0)
            );

          case "applications-high":
            return (
              Number(
                b.applicationCount || 0
              ) -
              Number(
                a.applicationCount || 0
              )
            );

          case "applications-low":
            return (
              Number(
                a.applicationCount || 0
              ) -
              Number(
                b.applicationCount || 0
              )
            );

          case "deadline":
            return (
              new Date(
                a.applicationDeadline ||
                  "9999-12-31"
              ) -
              new Date(
                b.applicationDeadline ||
                  "9999-12-31"
              )
            );

          case "newest":
          default:
            return (
              new Date(
                b.createdAt || 0
              ) -
              new Date(
                a.createdAt || 0
              )
            );
        }
      });

      return result;
    }, [
      jobs,
      filters,
      sortBy,
    ]);

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeFilterCount =
    Object.entries(filters).filter(
      ([key, value]) =>
        key !== "search" &&
        value !== "ALL"
    ).length +
    (filters.search ? 1 : 0);

  /* =======================================================
     RESET FILTERS
  ======================================================= */

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "ALL",
      location: "ALL",
      employment: "ALL",
      experience: "ALL",
      vacancy: "ALL",
      applications: "ALL",
    });

    setSortBy("newest");
  };

  /* =======================================================
     UPDATE STATUS
  ======================================================= */

  const handleStatusChange = async (
    jobId,
    newStatus
  ) => {
    if (!jobId || !newStatus) {
      return;
    }

    const currentJob =
      jobs.find(
        (job) =>
          Number(job.jobId) ===
          Number(jobId)
      );

    const oldStatus =
      currentJob?.status ||
      "PENDING";

    if (oldStatus === newStatus) {
      return;
    }

    const confirmResult =
      await Swal.fire({
        icon: "question",
        title: "Change Job Status?",
        text: `Change status from ${oldStatus} to ${newStatus}?`,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Change",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        confirmButtonColor:
          COLORS.primary,
      });

    if (
      !confirmResult.isConfirmed
    ) {
      return;
    }

    try {
      setUpdatingStatus(jobId);

      const response =
        await fetch(
          `${API_BASE}/jobs/${jobId}/status`,
          {
            method: "PATCH",
            headers: {
              "Content-Type":
                "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
            }),
          }
        );

      if (!response.ok) {
        let message =
          "Failed to update job status.";

        try {
          const data =
            await response.json();

          message =
            data?.message ||
            data?.error ||
            message;
        } catch {
          const text =
            await response.text();

          if (text) {
            message = text;
          }
        }

        throw new Error(message);
      }

      let updatedJob = null;

      try {
        updatedJob =
          await response.json();
      } catch {
        updatedJob = null;
      }

      setJobs((prev) =>
        prev.map((job) =>
          Number(job.jobId) ===
          Number(jobId)
            ? {
                ...job,
                status:
                  updatedJob?.status ||
                  newStatus,
              }
            : job
        )
      );

      await Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: `Job status changed to ${newStatus}.`,
        confirmButtonColor:
          COLORS.primary,
      });
    } catch (err) {
      console.error(
        "STATUS UPDATE ERROR:",
        err
      );

      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          err?.message ||
          "Unable to update job status.",
      });

      fetchJobs();
    } finally {
      setUpdatingStatus(null);
    }
  };

  /* =======================================================
     DELETE
  ======================================================= */

  const handleDelete = async (
    jobId
  ) => {
    const job =
      jobs.find(
        (item) =>
          Number(item.jobId) ===
          Number(jobId)
      );

    const result =
      await Swal.fire({
        icon: "warning",
        title: "Delete Job?",
        text: `Are you sure you want to delete "${job?.jobTitle || "this job"}"? This action cannot be undone.`,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        confirmButtonColor:
          "#dc2626",
      });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await jobService.deleteJob(
        jobId
      );

      setJobs((prev) =>
        prev.filter(
          (item) =>
            Number(item.jobId) !==
            Number(jobId)
        )
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Job deleted successfully.",
        confirmButtonColor:
          COLORS.primary,
      });
    } catch (err) {
      console.error(
        "DELETE JOB ERROR:",
        err
      );

      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          err?.response?.data
            ?.message ||
          err?.message ||
          "Unable to delete job.",
      });
    }
  };

  /* =======================================================
     VIEW
  ======================================================= */

  const handleViewClick = (
    job
  ) => {
    if (!job) return;

    console.log(
      "VIEW JOB:",
      job
    );

    setEditingJob(null);
    setSelectedJob(
      normalizeJob(job)
    );
  };

  /* =======================================================
     EDIT
  ======================================================= */

  const handleEditClick = (
    job
  ) => {
    if (!job) return;

    const normalized =
      normalizeJob(job);

    console.log(
      "EDIT JOB:",
      normalized
    );

    setSelectedJob(null);

    setEditingJob({
      jobId:
        normalized.jobId || "",

      jobTitle:
        normalized.jobTitle || "",

      jobDescription:
        normalized.jobDescription ||
        "",

      location:
        normalized.location || "",

      employeeType:
        normalized.employeeType ||
        "",

      experienceRequired:
        normalized.experienceRequired ||
        "",

      salaryMin:
        normalized.salaryMin ??
        "",

      salaryMax:
        normalized.salaryMax ??
        "",

      skillsRequired:
        normalized.skillsRequired ||
        "",

      vacancy:
        normalized.vacancy ??
        "",

      createdAt:
        normalized.createdAt || "",

      applicationDeadline:
        formatDateTimeLocal(
          normalized.applicationDeadline
        ),

      status:
        normalized.status ||
        "PENDING",

      applicationCount:
        normalized.applicationCount ??
        0,

      postedBy:
        normalized.postedBy ||
        null,
    });
  };

  /* =======================================================
     UPDATE JOB
  ======================================================= */

const handleUpdateJob = async () => {
  if (!editingJob) return;

  // Save karne se pehle validation
  if (
    !editingJob.jobTitle?.trim() ||
    !editingJob.jobDescription?.trim() ||
    !editingJob.location?.trim() ||
    !editingJob.employeeType?.trim() ||
    !editingJob.experienceRequired?.trim() ||
    !editingJob.skillsRequired?.trim() ||
    !editingJob.vacancy
  ) {
    await Swal.fire({
      icon: "warning",
      title: "Required Fields Missing",
      text: "Please fill all required fields.",
      confirmButtonColor: "#3E0F8D",
    });

    return;
  }

  // IMPORTANT:
  // Pehle edit modal close karo
  setEditingJob(null);

  // Ab confirmation alert open hoga
  const result = await Swal.fire({
    title: "Confirm Update",
    text: `Are you sure you want to save changes to "${editingJob.jobTitle}"?`,
    icon: "question",

    showCancelButton: true,

    confirmButtonText: "Yes, Save Changes",
    cancelButtonText: "Cancel",

    confirmButtonColor: "#3E0F8D",
    cancelButtonColor: "#777777",

    reverseButtons: true,

    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  // Cancel par kuch save nahi hoga
  if (!result.isConfirmed) {
    return;
  }

  setSavingJob(true);

  try {
    const payload = {
      jobTitle: editingJob.jobTitle.trim(),
      jobDescription: editingJob.jobDescription.trim(),
      location: editingJob.location.trim(),
      employeeType: editingJob.employeeType.trim(),
      experienceRequired: editingJob.experienceRequired.trim(),

      salaryMin:
        editingJob.salaryMin === ""
          ? null
          : Number(editingJob.salaryMin),

      salaryMax:
        editingJob.salaryMax === ""
          ? null
          : Number(editingJob.salaryMax),

      skillsRequired: editingJob.skillsRequired.trim(),

      vacancy: Number(editingJob.vacancy),

      applicationDeadline:
        editingJob.applicationDeadline || null,
    };

    console.log("UPDATE PAYLOAD:", payload);

    const response = await fetch(
      `/api/jobs/${editingJob.jobId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    const contentType =
      response.headers.get("content-type") || "";

    let responseData;

    if (contentType.includes("application/json")) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      throw new Error(
        typeof responseData === "string"
          ? responseData
          : responseData?.message || "Failed to update job"
      );
    }

    // Current list ko update karo
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        Number(job.jobId) === Number(editingJob.jobId)
          ? {
              ...job,
              ...payload,
            }
          : job
      )
    );

    // Success alert
    await Swal.fire({
      icon: "success",
      title: "Job Updated Successfully!",
      text: "Your job details have been saved.",
      confirmButtonColor: "#3E0F8D",
      timer: 1800,
      timerProgressBar: true,
    });

    // Latest data backend se reload
    await fetchJobs();

  } catch (error) {
    console.error("UPDATE JOB ERROR:", error);

    await Swal.fire({
      icon: "error",
      title: "Update Failed",
      text:
        error?.message ||
        "Something went wrong while updating the job.",
      confirmButtonColor: "#3E0F8D",
    });

  } finally {
    setSavingJob(false);
  }
};


  /* =======================================================
     STATUS CLASS
  ======================================================= */

  const getStatusClass = (
    status
  ) => {
    switch (
      String(status || "")
        .toUpperCase()
    ) {
      case "APPROVED":
        return "approved";

      case "REJECTED":
        return "rejected";

      case "BLOCKED":
        return "blocked";

      case "INACTIVE":
        return "inactive";

      case "PENDING":
      default:
        return "pending";
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
    <PartnerHeader />

    {/* Breadcrumb */}

        {/* <div className="breadcrumb">
          <button
            type="button"
            onClick={() => navigate("/partner-jobs")}
          >
            My Jobs
          </button>

          <ChevronRight size={15} />

          <span>Create Job</span>
        </div> */}

      <Page>


              {/* <PartnerHeader
                user={loggedInUser}
                userRole={userRole}
              /> */}

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="jobs-header">

          <div className="jobs-header-left">

            <div className="jobs-header-icon">
              <BriefcaseBusiness
                size={21}
              />
            </div>

            <div>
              <h2>
                My Jobs
              </h2>

              <p>
                Manage and monitor jobs posted
                by your account
              </p>
            </div>

          </div>

          <div className="header-actions">

            <button
              type="button"
              className="filter-toggle-btn"
              onClick={() =>
                setShowFilters(
                  (prev) => !prev
                )
              }
            >
              <SlidersHorizontal
                size={15}
              />

              Filters

              {activeFilterCount >
                0 && (
                <span className="filter-count">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              type="button"
              className="refresh-btn"
              onClick={fetchJobs}
              disabled={loading}
            >
              <RefreshCw
                size={15}
                className={
                  loading
                    ? "refresh-spin"
                    : ""
                }
              />

              Refresh
            </button>

          </div>

        </div>

        {/* =================================================
            FILTERS
        ================================================= */}

        {showFilters && (
          <div className="filters-panel">

            <div className="filter-search">

              <Search
                size={16}
              />

              <input
                type="text"
                placeholder="Search job title, ID, skills, location..."
                value={
                  filters.search
                }
                onChange={(e) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      search:
                        e.target.value,
                    })
                  )
                }
              />

              {filters.search && (
                <button
                  type="button"
                  onClick={() =>
                    setFilters(
                      (prev) => ({
                        ...prev,
                        search: "",
                      })
                    )
                  }
                >
                  <X size={14} />
                </button>
              )}

            </div>

            <div className="filter-grid">

              <FilterSelect
                label="Status"
                value={
                  filters.status
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      status: value,
                    })
                  )
                }
                options={[
                  "ALL",
                  ...STATUS_OPTIONS,
                ]}
              />

              <FilterSelect
                label="Location"
                value={
                  filters.location
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      location: value,
                    })
                  )
                }
                options={[
                  "ALL",
                  ...locationOptions,
                ]}
              />

              <FilterSelect
                label="Employee Type"
                value={
                  filters.employment
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      employment: value,
                    })
                  )
                }
                options={[
                  "ALL",
                  ...employmentOptions,
                ]}
              />

              <FilterSelect
                label="Experience"
                value={
                  filters.experience
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      experience:
                        value,
                    })
                  )
                }
                options={[
                  "ALL",
                  "0-2",
                  "3-5",
                  "6-10",
                  "10+",
                ]}
              />

              <FilterSelect
                label="Vacancy"
                value={
                  filters.vacancy
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      vacancy: value,
                    })
                  )
                }
                options={[
                  "ALL",
                  "1-5",
                  "6-10",
                  "11+",
                ]}
              />

              <FilterSelect
                label="Applications"
                value={
                  filters.applications
                }
                onChange={(value) =>
                  setFilters(
                    (prev) => ({
                      ...prev,
                      applications:
                        value,
                    })
                  )
                }
                options={[
                  "ALL",
                  "0",
                  "1-10",
                  "11-50",
                  "51+",
                ]}
              />

              <FilterSelect
                label="Sort By"
                value={sortBy}
                onChange={setSortBy}
                options={[
                  "newest",
                  "oldest",
                  "title",
                  "vacancy-high",
                  "applications-high",
                  "applications-low",
                  "deadline",
                ]}
                formatOption={(value) => {
                  const labels = {
                    newest:
                      "Newest First",
                    oldest:
                      "Oldest First",
                    title:
                      "Title A-Z",
                    "vacancy-high":
                      "Vacancy High",
                    "applications-high":
                      "Applications High",
                    "applications-low":
                      "Applications Low",
                    deadline:
                      "Deadline",
                  };

                  return (
                    labels[value] ||
                    value
                  );
                }}
              />

            </div>

            <div className="filter-bottom">

              <span>
                Showing{" "}
                <strong>
                  {
                    filteredJobs.length
                  }
                </strong>{" "}
                of{" "}
                <strong>
                  {jobs.length}
                </strong>{" "}
                jobs
              </span>

              {activeFilterCount >
                0 && (
                <button
                  type="button"
                  className="reset-filter-btn"
                  onClick={
                    resetFilters
                  }
                >
                  <RotateCcw
                    size={13}
                  />

                  Reset Filters
                </button>
              )}

            </div>

          </div>
        )}

        {/* =================================================
            ERROR
        ================================================= */}

        {error && (
          <div className="jobs-error">

            <div className="jobs-error-content">

              <AlertCircle
                size={18}
              />

              <span>
                {error}
              </span>

            </div>

            <button
              type="button"
              onClick={fetchJobs}
            >
              Try Again
            </button>

          </div>
        )}

        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (
          <div className="jobs-loading">

            <RefreshCw
              size={28}
              className="refresh-spin"
            />

            <p>
              Loading your jobs...
            </p>

          </div>
        ) : filteredJobs.length ===
          0 ? (
          <div className="jobs-empty">

            <div className="empty-icon">
              <BriefcaseBusiness
                size={42}
              />
            </div>

            <h3>
              {jobs.length ===
              0
                ? "No Jobs Found"
                : "No Matching Jobs"}
            </h3>

            <p>
              {jobs.length ===
              0
                ? "You haven't posted any jobs yet."
                : "Try changing your filters."}
            </p>

            {jobs.length ===
            0 ? (
              <button
                type="button"
                className="create-job-btn"
                onClick={() =>
                  window.location.href =
                    "/create-job"
                }
              >
                <BriefcaseBusiness
                  size={15}
                />

                Create New Job
              </button>
            ) : (
              <button
                type="button"
                className="create-job-btn"
                onClick={
                  resetFilters
                }
              >
                <RotateCcw
                  size={15}
                />

                Reset Filters
              </button>
            )}

          </div>
        ) : (
          /* =================================================
             TABLE
          ================================================= */

          <div className="jobs-table-wrapper">

            <table className="jobs-table">

              <thead>
                <tr>

                  <th>
                    Job
                  </th>

                  <th>
                    Skills
                  </th>

                  <th>
                    Location
                  </th>

                  <th>
                    Employee Type
                  </th>

                  <th>
                    Experience
                  </th>

                  <th>
                    Salary
                  </th>

                  <th>
                    Vacancy
                  </th>

                  <th>
                    Applications
                  </th>

                  <th>
                    Deadline
                  </th>

                  <th>
                    Status
                  </th>

                  <th>
                    Actions
                  </th>

                </tr>
              </thead>

              <tbody>

                {filteredJobs.map(
                  (job) => {
                    const currentStatus =
                      job.status ||
                      "PENDING";

                    const isUpdating =
                      Number(
                        updatingStatus
                      ) ===
                      Number(
                        job.jobId
                      );

                    return (
                      <tr
                        key={
                          job.jobId
                        }
                      >

                        {/* JOB */}

                        <td>

                          <div className="job-title-cell">

                            <div className="job-icon">
                              <BriefcaseBusiness
                                size={17}
                              />
                            </div>

                            <div>

                              <strong>
                                {job.jobTitle ||
                                  "Untitled Job"}
                              </strong>

                              <small>
                                Job ID:{" "}
                                {job.jobId ||
                                  "-"}
                              </small>

                            </div>

                          </div>

                        </td>

                        {/* SKILLS */}

                        <td>

                          <div className="skills-cell">

                            {job.skillsRequired ||
                              "-"}

                          </div>

                        </td>

                        {/* LOCATION */}

                        <td>

                          <div className="location-cell">

                            <MapPin
                              size={14}
                            />

                            {job.location ||
                              "-"}

                          </div>

                        </td>

                        {/* EMPLOYEE TYPE */}

                        <td>

                          <div className="employment-cell">

                            <Building2
                              size={14}
                            />

                            {job.employeeType ||
                              "-"}

                          </div>

                        </td>

                        {/* EXPERIENCE */}

                        <td>

                          <div className="experience-cell">

                            <Clock3
                              size={14}
                            />

                            {job.experienceRequired ||
                              "-"}

                          </div>

                        </td>

                        {/* SALARY */}

                        <td>

                          <div className="salary-cell">

                            <IndianRupee
                              size={14}
                            />

                            {formatSalary(
                              job.salaryMin,
                              job.salaryMax
                            )}

                          </div>

                        </td>

                        {/* VACANCY */}

                        <td>

                          <div className="vacancy-cell">

                            <Users
                              size={15}
                            />

                            {job.vacancy ??
                              0}

                          </div>

                        </td>

                        {/* APPLICATIONS */}

                        <td>

                          <span className="application-count">

                            {job.applicationCount ??
                              0}

                          </span>

                        </td>

                        {/* DEADLINE */}

                        <td>

                          <div className="deadline-cell">

                            <CalendarDays
                              size={14}
                            />

                            {formatDateOnly(
                              job.applicationDeadline
                            )}

                          </div>

                        </td>

                        {/* STATUS */}

                        <td>

                          <div className="status-wrapper">

                            <select
                              value={
                                currentStatus
                              }
                              disabled={
                                isUpdating
                              }
                              className={`status-select ${getStatusClass(
                                currentStatus
                              )}`}
                              onChange={(
                                e
                              ) =>
                                handleStatusChange(
                                  job.jobId,
                                  e
                                    .target
                                    .value
                                )
                              }
                            >

                              {STATUS_OPTIONS.map(
                                (
                                  status
                                ) => (
                                  <option
                                    key={
                                      status
                                    }
                                    value={
                                      status
                                    }
                                  >
                                    {
                                      status
                                    }
                                  </option>
                                )
                              )}

                            </select>

                            {isUpdating && (
                              <RefreshCw
                                size={13}
                                className="status-loading refresh-spin"
                              />
                            )}

                          </div>

                        </td>

                        {/* ACTIONS */}

                        <td>

                          <div className="job-actions">

                            {/* VIEW */}

                            <button
                              type="button"
                              className="job-action-btn view"
                              title="View Job"
                              onClick={() =>
                                handleViewClick(
                                  job
                                )
                              }
                            >
                              <Eye
                                size={16}
                              />
                            </button>

                            {/* EDIT */}

                            <button
                              type="button"
                              className="job-action-btn edit"
                              title="Edit Job"
                              onClick={() =>
                                handleEditClick(
                                  job
                                )
                              }
                            >
                              <Pencil
                                size={16}
                              />
                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="job-action-btn delete"
                              title="Delete Job"
                              onClick={() =>
                                handleDelete(
                                  job.jobId
                                )
                              }
                            >
                              <Trash2
                                size={16}
                              />
                            </button>

                          </div>

                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>
        )}
      {/* =====================================================
          PARTNER FOOTER
      ===================================================== */}

      
 </Page>
<PartnerFooter />
      {/* =====================================================
          VIEW DRAWER
      ===================================================== */}

      {selectedJob &&
        typeof document !==
          "undefined" &&
        createPortal(
          <div
            className="job-preview-overlay"
            onMouseDown={(e) => {
              if (
                e.target ===
                e.currentTarget
              ) {
                setSelectedJob(
                  null
                );
              }
            }}
          >

            <div
              className="job-preview-drawer"
              onMouseDown={(e) =>
                e.stopPropagation()
              }
            >

              {/* DRAWER HEADER */}

              <div className="preview-header">

                <div className="preview-header-left">

                  <div className="preview-icon">
                    <BriefcaseBusiness
                      size={22}
                    />
                  </div>

                  <div>

                    <h2>
                      {selectedJob.jobTitle ||
                        "Untitled Job"}
                    </h2>

                    <span>
                      Job ID:{" "}
                      {selectedJob.jobId ||
                        "-"}
                    </span>

                  </div>

                </div>

                <button
                  type="button"
                  className="preview-close-btn"
                  onClick={() =>
                    setSelectedJob(
                      null
                    )
                  }
                >
                  <X
                    size={20}
                  />
                </button>

              </div>

              {/* DRAWER BODY */}

              <div className="preview-body">

                {/* STATUS */}

                <div className="preview-status-row">

                  <span>
                    Current Status
                  </span>

                  <strong
                    className={`preview-status ${getStatusClass(
                      selectedJob.status
                    )}`}
                  >
                    {selectedJob.status ||
                      "PENDING"}
                  </strong>

                </div>

                {/* JOB INFORMATION */}

                <div className="preview-section">

                  <h3>
                    <FileText
                      size={17}
                    />

                    Job Information
                  </h3>

                  <div className="preview-grid">

                    <PreviewItem
                      label="Job ID"
                      value={
                        selectedJob.jobId
                      }
                    />

                    <PreviewItem
                      label="Posted By"
                      value={
                        getPostedById(
                          selectedJob
                        )
                      }
                    />

                    <PreviewItem
                      label="Job Title"
                      value={
                        selectedJob.jobTitle
                      }
                    />

                    <PreviewItem
                      label="Location"
                      value={
                        selectedJob.location
                      }
                      icon={
                        <MapPin
                          size={14}
                        />
                      }
                    />

                    <PreviewItem
                      label="Employee Type"
                      value={
                        selectedJob.employeeType
                      }
                      icon={
                        <Building2
                          size={14}
                        />
                      }
                    />

                    <PreviewItem
                      label="Experience Required"
                      value={
                        selectedJob.experienceRequired
                      }
                      icon={
                        <Clock3
                          size={14}
                        />
                      }
                    />

                    <PreviewItem
                      label="Vacancy"
                      value={
                        selectedJob.vacancy ??
                        0
                      }
                      icon={
                        <Users
                          size={14}
                        />
                      }
                    />

                    <PreviewItem
                      label="Application Count"
                      value={
                        selectedJob.applicationCount ??
                        0
                      }
                    />

                    <PreviewItem
                      label="Created At"
                      value={formatDisplayDate(
                        selectedJob.createdAt
                      )}
                      icon={
                        <CalendarDays
                          size={14}
                        />
                      }
                    />

                    <PreviewItem
                      label="Application Deadline"
                      value={formatDisplayDate(
                        selectedJob.applicationDeadline
                      )}
                      icon={
                        <CalendarDays
                          size={14}
                        />
                      }
                    />

                  </div>

                </div>

                {/* SALARY */}

                <div className="preview-section">

                  <h3>
                    <IndianRupee
                      size={17}
                    />

                    Salary
                  </h3>

                  <div className="salary-preview">

                    {formatSalary(
                      selectedJob.salaryMin,
                      selectedJob.salaryMax
                    )}

                  </div>

                </div>

                {/* SKILLS */}

                <div className="preview-section">

                  <h3>
                    Skills Required
                  </h3>

                  <div className="skills-preview">

                    {selectedJob.skillsRequired
                      ?.split(",")
                      .map(
                        (
                          skill,
                          index
                        ) => {
                          const value =
                            skill.trim();

                          if (
                            !value
                          ) {
                            return null;
                          }

                          return (
                            <span
                              key={
                                index
                              }
                            >
                              {
                                value
                              }
                            </span>
                          );
                        }
                      )}

                    {!selectedJob.skillsRequired && (
                      <span className="no-data">
                        No skills specified
                      </span>
                    )}

                  </div>

                </div>

                {/* DESCRIPTION */}

                <div className="preview-section">

                  <h3>
                    Job Description
                  </h3>

                  <div className="preview-description">

                    {selectedJob.jobDescription ||
                      "No job description provided."}

                  </div>

                </div>

              </div>

              {/* DRAWER FOOTER */}

              <div className="preview-footer">

                <button
                  type="button"
                  className="preview-cancel-btn"
                  onClick={() =>
                    setSelectedJob(
                      null
                    )
                  }
                >
                  Close
                </button>

                <button
                  type="button"
                  className="preview-edit-btn"
                  onClick={() => {
                    const job =
                      selectedJob;

                    setSelectedJob(
                      null
                    );

                    setTimeout(() => {
                      handleEditClick(
                        job
                      );
                    }, 50);
                  }}
                >
                  <Pencil
                    size={16}
                  />

                  Edit Job
                </button>

              </div>

            </div>

          </div>,
          document.body
        )}

      {/* =====================================================
          EDIT MODAL
      ===================================================== */}

      {editingJob &&
        typeof document !==
          "undefined" &&
        createPortal(
          <div
            className="edit-job-overlay"
            onMouseDown={(e) => {
              if (
                e.target ===
                  e.currentTarget &&
                !savingJob
              ) {
                setEditingJob(
                  null
                );
              }
            }}
          >

            <div
              className="edit-job-modal"
              onMouseDown={(e) =>
                e.stopPropagation()
              }
            >

              {/* MODAL HEADER */}

              <div className="edit-modal-header">

                <div className="edit-header-left">

                  <div className="edit-header-icon">
                    <Pencil
                      size={20}
                    />
                  </div>

                  <div>

                    <h2>
                      Edit Job
                    </h2>

                    <p>
                      Job ID:{" "}
                      {editingJob.jobId}
                    </p>

                  </div>

                </div>

                <button
                  type="button"
                  className="modal-close-btn"
                  disabled={
                    savingJob
                  }
                  onClick={() =>
                    setEditingJob(
                      null
                    )
                  }
                >
                  <X
                    size={20}
                  />
                </button>

              </div>

              {/* MODAL BODY */}

              <div className="edit-modal-body">

                {/* JOB TITLE */}

                <div className="form-group">

                  <label>
                    Job Title *
                  </label>

                  <input
                    type="text"
                    value={
                      editingJob.jobTitle
                    }
                    onChange={(e) =>
                      setEditingJob(
                        (prev) => ({
                          ...prev,
                          jobTitle:
                            e.target
                              .value,
                        })
                      )
                    }
                    placeholder="Enter job title"
                  />

                </div>

                {/* DESCRIPTION */}

                <div className="form-group">

                  <label>
                    Job Description *
                  </label>

                  <textarea
                    rows={6}
                    value={
                      editingJob.jobDescription
                    }
                    onChange={(e) =>
                      setEditingJob(
                        (prev) => ({
                          ...prev,
                          jobDescription:
                            e.target
                              .value,
                        })
                      )
                    }
                    placeholder="Enter complete job description"
                  />

                </div>

                {/* GRID */}

                <div className="form-grid">

                  {/* LOCATION */}

                  <div className="form-group">

                    <label>
                      Location *
                    </label>

                    <input
                      type="text"
                      value={
                        editingJob.location
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            location:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="e.g. Gurugram"
                    />

                  </div>

                  {/* EMPLOYEE TYPE */}

                  <div className="form-group">

                    <label>
                      Employee Type *
                    </label>

                    <input
                      type="text"
                      value={
                        editingJob.employeeType
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            employeeType:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="e.g. Full Time"
                    />

                  </div>

                  {/* EXPERIENCE */}

                  <div className="form-group">

                    <label>
                      Experience Required *
                    </label>

                    <input
                      type="text"
                      value={
                        editingJob.experienceRequired
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            experienceRequired:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="e.g. 2-5 Years"
                    />

                  </div>

                  {/* VACANCY */}

                  <div className="form-group">

                    <label>
                      Vacancy *
                    </label>

                    <input
                      type="number"
                      min="1"
                      value={
                        editingJob.vacancy
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            vacancy:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="Number of vacancies"
                    />

                  </div>

                  {/* SALARY MIN */}

                  <div className="form-group">

                    <label>
                      Minimum Salary
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        editingJob.salaryMin
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            salaryMin:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="e.g. 30000"
                    />

                  </div>

                  {/* SALARY MAX */}

                  <div className="form-group">

                    <label>
                      Maximum Salary
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        editingJob.salaryMax
                      }
                      onChange={(e) =>
                        setEditingJob(
                          (prev) => ({
                            ...prev,
                            salaryMax:
                              e.target
                                .value,
                          })
                        )
                      }
                      placeholder="e.g. 60000"
                    />

                  </div>

                </div>

                {/* SKILLS */}

                <div className="form-group">

                  <label>
                    Skills Required
                  </label>

                  <input
                    type="text"
                    value={
                      editingJob.skillsRequired
                    }
                    onChange={(e) =>
                      setEditingJob(
                        (prev) => ({
                          ...prev,
                          skillsRequired:
                            e.target
                              .value,
                        })
                      )
                    }
                    placeholder="Java, Spring Boot, SQL, React"
                  />

                  <small>
                    Enter skills separated by
                    commas.
                  </small>

                </div>

                {/* DEADLINE */}

                <div className="form-group">

                  <label>
                    Application Deadline
                  </label>

                  <input
                    type="datetime-local"
                    value={
                      editingJob.applicationDeadline
                    }
                    onChange={(e) =>
                      setEditingJob(
                        (prev) => ({
                          ...prev,
                          applicationDeadline:
                            e.target
                              .value,
                        })
                      )
                    }
                  />

                </div>

                {/* READ ONLY DATA */}

                <div className="readonly-heading">
                  Job System Information
                </div>

                <div className="readonly-info">

                  <div>
                    <span>
                      Job ID
                    </span>

                    <strong>
                      {
                        editingJob.jobId
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Status
                    </span>

                    <strong>
                      {
                        editingJob.status
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Applications
                    </span>

                    <strong>
                      {
                        editingJob.applicationCount ??
                        0
                      }
                    </strong>
                  </div>

                  <div>
                    <span>
                      Created At
                    </span>

                    <strong>
                      {formatDisplayDate(
                        editingJob.createdAt
                      )}
                    </strong>
                  </div>

                </div>

              </div>

              {/* MODAL FOOTER */}

              <div className="edit-modal-footer">

                <button
                  type="button"
                  className="modal-cancel-btn"
                  disabled={
                    savingJob
                  }
                  onClick={() =>
                    setEditingJob(
                      null
                    )
                  }
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="modal-save-btn"
                  disabled={
                    savingJob
                  }
                  onClick={
                    handleUpdateJob
                  }
                >

                  {savingJob ? (
                    <>
                      <RefreshCw
                        size={16}
                        className="refresh-spin"
                      />

                      Saving...
                    </>
                  ) : (
                    <>
                      <Save
                        size={16}
                      />

                      Save Changes
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>,
          document.body
        )}

      <style>
        {tableCSS}
      </style>


      

    </>
  );
}

/* =========================================================
   FILTER SELECT
========================================================= */

const FilterSelect = ({
  label,
  value,
  onChange,
  options,
  formatOption,
}) => {
  return (
    <div className="filter-item">

      <label>
        {label}
      </label>

      <select
        value={value}
        onChange={(e) =>
          onChange(
            e.target.value
          )
        }
      >
        {options.map(
          (option) => (
            <option
              key={option}
              value={option}
            >
              {formatOption
                ? formatOption(
                    option
                  )
                : option}
            </option>
          )
        )}
      </select>

    </div>
  );
};

/* =========================================================
   PREVIEW ITEM
========================================================= */

const PreviewItem = ({
  label,
  value,
  icon,
}) => {
  return (
    <div className="preview-item">

      <label>
        {label}
      </label>

      <strong>

        {icon && (
          <span className="preview-value-icon">
            {icon}
          </span>
        )}

        {value !== undefined &&
        value !== null &&
        value !== ""
          ? String(value)
          : "-"}

      </strong>

    </div>
  );
};

/* =========================================================
   PAGE COMPONENT
========================================================= */

const Page = ({
  title,
  children,
}) => {
  return (
    <div className="my-jobs-page">

      <div className="my-jobs-title">
        <h1>
          {title}
        </h1>
      </div>

      <div className="my-jobs-card">
        {children}
      </div>

    </div>
  );
};

/* =========================================================
   CSS
========================================================= */

const tableCSS = `

/* =====================================================
   GLOBAL
===================================================== */

* {
  box-sizing: border-box;
}

/* =====================================================
   PAGE
===================================================== */

.my-jobs-page {
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  background: #f5f3f8;
}

.my-jobs-title {
  margin-bottom: 16px;
}

.my-jobs-title h1 {
  margin: 0;
  color: #21152d;
  font-size: 24px;
  font-weight: 750;
}

/* =====================================================
   CARD
===================================================== */

.my-jobs-card {
  width: 100%;
  background: #ffffff;
  padding: 22px;
  border-radius: 15px;
  border: 1px solid #e6e0ee;
  box-shadow:
    0 5px 22px rgba(62, 15, 141, .06);
}

/* =====================================================
   HEADER
===================================================== */

.jobs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
  padding-bottom: 17px;
  border-bottom: 1px solid #eee9f3;
}

.jobs-header-left {
  display: flex;
  align-items: center;
  gap: 11px;
}

.jobs-header-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 10px;
  background: #f0eafa;
  color: #3E0F8D;
}

.jobs-header h2 {
  margin: 0 0 4px;
  color: #24152f;
  font-size: 18px;
  font-weight: 750;
}

.jobs-header p {
  margin: 0;
  color: #7c7087;
  font-size: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* =====================================================
   BUTTONS
===================================================== */

.refresh-btn,
.filter-toggle-btn {
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 13px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 650;
  transition: .2s ease;
}

.refresh-btn {
  border: 1px solid #dcd2e9;
  background: #fff;
  color: #3E0F8D;
}

.refresh-btn:hover {
  background: #f7f3fb;
  border-color: #9564DD;
}

.refresh-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.filter-toggle-btn {
  border: 1px solid #d8cbe7;
  background: #f7f3fb;
  color: #3E0F8D;
}

.filter-toggle-btn:hover {
  background: #eee7f8;
}

.filter-count {
  min-width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border-radius: 20px;
  background: #3E0F8D;
  color: white;
  font-size: 9px;
}

/* =====================================================
   FILTER PANEL
===================================================== */

.filters-panel {
  margin-bottom: 18px;
  padding: 15px;
  border: 1px solid #e4ddec;
  border-radius: 11px;
  background: #fbfaff;
}

.filter-search {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 11px;
  margin-bottom: 14px;
  border: 1px solid #ddd5e7;
  border-radius: 8px;
  background: #fff;
  color: #8b7c98;
}

.filter-search:focus-within {
  border-color: #9564DD;
  box-shadow:
    0 0 0 3px rgba(149,100,221,.10);
}

.filter-search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #30243a;
  font-size: 12px;
}

.filter-search input::placeholder {
  color: #aaa0b1;
}

.filter-search button {
  border: none;
  background: transparent;
  color: #9a8da4;
  cursor: pointer;
}

.filter-grid {
  display: grid;
  grid-template-columns:
    repeat(7, minmax(110px, 1fr));
  gap: 10px;
}

.filter-item label {
  display: block;
  margin-bottom: 5px;
  color: #71627e;
  font-size: 10px;
  font-weight: 700;
}

.filter-item select {
  width: 100%;
  height: 35px;
  padding: 0 8px;
  border: 1px solid #ddd5e7;
  border-radius: 7px;
  outline: none;
  background: #fff;
  color: #42344d;
  font-size: 11px;
  cursor: pointer;
}

.filter-item select:focus {
  border-color: #9564DD;
}

.filter-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 13px;
  color: #85788f;
  font-size: 11px;
}

.filter-bottom strong {
  color: #3E0F8D;
}

.reset-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  color: #3E0F8D;
  font-size: 11px;
  font-weight: 650;
  cursor: pointer;
}

/* =====================================================
   TABLE
===================================================== */

.jobs-table-wrapper {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e6e0ec;
  border-radius: 10px;
}

.jobs-table {
  width: 100%;
  min-width: 1450px;
  border-collapse: collapse;
  background: #fff;
}

.jobs-table th {
  padding: 12px 12px;
  background: #f8f6fa;
  border-bottom: 1px solid #e4dfe8;
  color: #665a70;
  font-size: 10px;
  font-weight: 800;
  text-align: left;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: .25px;
}

.jobs-table td {
  padding: 13px 12px;
  border-bottom: 1px solid #eeeaf1;
  color: #4a4051;
  font-size: 12px;
  vertical-align: middle;
}

.jobs-table tbody tr {
  transition: .15s ease;
}

.jobs-table tbody tr:hover {
  background: #fcfaff;
}

.jobs-table tbody tr:last-child td {
  border-bottom: none;
}

/* =====================================================
   JOB TITLE
===================================================== */

.job-title-cell {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 210px;
}

.job-icon {
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f0eafa;
  color: #3E0F8D;
}

.job-title-cell strong {
  display: block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #30233a;
  font-size: 12px;
  font-weight: 750;
}

.job-title-cell small {
  display: block;
  margin-top: 3px;
  color: #9a8da3;
  font-size: 9px;
}

/* =====================================================
   SKILLS
===================================================== */

.skills-cell {
  max-width: 220px;
  line-height: 1.5;
  color: #665a70;
}

/* =====================================================
   LOCATION
===================================================== */

.location-cell,
.employment-cell,
.experience-cell,
.vacancy-cell,
.deadline-cell,
.salary-cell {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #665a70;
  white-space: nowrap;
}

.location-cell svg,
.employment-cell svg,
.experience-cell svg,
.vacancy-cell svg,
.deadline-cell svg,
.salary-cell svg {
  color: #9564DD;
  flex-shrink: 0;
}

.salary-cell {
  font-weight: 650;
}

/* =====================================================
   APPLICATION
===================================================== */

.application-count {
  min-width: 31px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 7px;
  background: #f1ebf8;
  color: #3E0F8D;
  font-size: 11px;
  font-weight: 750;
}

/* =====================================================
   STATUS
===================================================== */

.status-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.status-select {
  min-width: 105px;
  height: 30px;
  padding: 0 23px 0 9px;
  border-radius: 20px;
  border: 1px solid;
  outline: none;
  font-size: 9px;
  font-weight: 800;
  cursor: pointer;
}

.status-select:disabled {
  opacity: .65;
  cursor: wait;
}

.status-select.pending {
  color: #936b00;
  background: #fffbed;
  border-color: #e8cf67;
}

.status-select.approved {
  color: #157347;
  background: #effaf3;
  border-color: #91d5aa;
}

.status-select.rejected {
  color: #bd2929;
  background: #fff1f1;
  border-color: #efabab;
}

.status-select.blocked {
  color: #7b1e1e;
  background: #fff0f0;
  border-color: #ee8c8c;
}

.status-select.inactive {
  color: #667085;
  background: #f2f3f5;
  border-color: #d0d5dd;
}

.status-loading {
  position: absolute;
  right: 7px;
  pointer-events: none;
  color: #7b6a87;
}

/* =====================================================
   ACTIONS
===================================================== */

.job-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.job-action-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 7px;
  border: 1px solid #ded6e6;
  background: #fff;
  cursor: pointer;
  transition: .2s ease;
}

.job-action-btn:hover {
  transform: translateY(-1px);
}

.job-action-btn.view {
  color: #3E0F8D;
}

.job-action-btn.view:hover {
  background: #f0eafa;
  border-color: #b99dde;
}

.job-action-btn.edit {
  color: #a97800;
}

.job-action-btn.edit:hover {
  background: #fffbea;
  border-color: #e5ca69;
}

.job-action-btn.delete {
  color: #d02c2c;
}

.job-action-btn.delete:hover {
  background: #fff1f1;
  border-color: #eeaaaa;
}

/* =====================================================
   LOADING
===================================================== */

.jobs-loading {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #7b6d85;
}

.jobs-loading p {
  margin-top: 10px;
  font-size: 13px;
}

/* =====================================================
   EMPTY
===================================================== */

.jobs-empty {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-icon {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  background: #f0eafa;
  color: #3E0F8D;
}

.jobs-empty h3 {
  margin: 14px 0 5px;
  color: #403348;
  font-size: 17px;
}

.jobs-empty p {
  margin: 0 0 18px;
  color: #9a8da4;
  font-size: 12px;
}

.create-job-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  padding: 9px 15px;
  border-radius: 8px;
  background: #3E0F8D;
  color: #fff;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}

.create-job-btn:hover {
  background: #2e086c;
}

/* =====================================================
   ERROR
===================================================== */

.jobs-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 15px;
  padding: 11px 14px;
  border: 1px solid #f2c2c2;
  border-radius: 8px;
  background: #fff4f4;
  color: #b42318;
  font-size: 12px;
}

.jobs-error-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.jobs-error button {
  border: none;
  background: transparent;
  color: #3E0F8D;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}

/* =====================================================
   PREVIEW OVERLAY
===================================================== */

.job-preview-overlay {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 999999 !important;
  background: rgba(27, 14, 38, .58);
  backdrop-filter: blur(4px);
}

/* =====================================================
   PREVIEW DRAWER
===================================================== */

.job-preview-drawer {
  position: absolute;
  top: 0;
  right: 0;
  width: min(720px, 96vw);
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow:
    -20px 0 70px rgba(30, 10, 50, .25);
  animation: drawerIn .25s ease;
}

@keyframes drawerIn {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}

/* =====================================================
   PREVIEW HEADER
===================================================== */

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 18px 21px;
  background: #3E0F8D;
  color: #fff;
}

.preview-header-left {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.preview-icon {
  width: 43px;
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 10px;
  background: rgba(255,255,255,.14);
}

.preview-header h2 {
  max-width: 480px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
}

.preview-header span {
  display: block;
  margin-top: 4px;
  opacity: .78;
  font-size: 11px;
}

.preview-close-btn,
.modal-close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: rgba(255,255,255,.12);
  color: #fff;
  cursor: pointer;
}

.preview-close-btn:hover,
.modal-close-btn:hover {
  background: rgba(255,255,255,.22);
}

/* =====================================================
   PREVIEW BODY
===================================================== */

.preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 21px;
}

.preview-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee9f2;
}

.preview-status-row > span {
  color: #786b81;
  font-size: 12px;
}

.preview-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 800;
}

.preview-status.pending {
  color: #936b00;
  background: #fffbed;
}

.preview-status.approved {
  color: #157347;
  background: #effaf3;
}

.preview-status.rejected {
  color: #bd2929;
  background: #fff1f1;
}

.preview-status.blocked {
  color: #7b1e1e;
  background: #fff0f0;
}

.preview-status.inactive {
  color: #667085;
  background: #f2f3f5;
}

/* =====================================================
   PREVIEW SECTION
===================================================== */

.preview-section {
  margin-bottom: 23px;
}

.preview-section h3 {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 12px;
  color: #3E0F8D;
  font-size: 14px;
  font-weight: 750;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.preview-item {
  min-height: 62px;
  padding: 11px;
  border: 1px solid #e9e4ef;
  border-radius: 9px;
  background: #fbfaff;
}

.preview-item label {
  display: block;
  margin-bottom: 5px;
  color: #91859a;
  font-size: 10px;
}

.preview-item strong {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #382c41;
  font-size: 12px;
  line-height: 1.4;
}

.preview-value-icon {
  color: #9564DD;
  display: inline-flex;
}

/* =====================================================
   SALARY
===================================================== */

.salary-preview {
  padding: 14px;
  border-radius: 10px;
  background: #f2edf8;
  color: #3E0F8D;
  font-size: 18px;
  font-weight: 800;
}

/* =====================================================
   SKILLS
===================================================== */

.skills-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.skills-preview span {
  padding: 6px 10px;
  border-radius: 20px;
  background: #eee7f7;
  color: #3E0F8D;
  font-size: 10px;
  font-weight: 700;
}

.skills-preview .no-data {
  color: #91859a;
  background: #f3f3f4;
}

/* =====================================================
   DESCRIPTION
===================================================== */

.preview-description {
  padding: 14px;
  border: 1px solid #e8e2ed;
  border-radius: 10px;
  background: #fff;
  color: #5d5265;
  font-size: 12px;
  line-height: 1.75;
  white-space: pre-wrap;
}

/* =====================================================
   PREVIEW FOOTER
===================================================== */

.preview-footer,
.edit-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 15px 20px;
  border-top: 1px solid #ebe7ef;
  background: #fff;
}

.preview-cancel-btn,
.modal-cancel-btn {
  height: 37px;
  padding: 0 15px;
  border: 1px solid #ddd6e5;
  border-radius: 8px;
  background: #fff;
  color: #665a70;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}

.preview-edit-btn,
.modal-save-btn {
  height: 37px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: #3E0F8D;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.preview-edit-btn:hover,
.modal-save-btn:hover {
  background: #2e086c;
}

.modal-cancel-btn:disabled,
.modal-save-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

/* =====================================================
   EDIT OVERLAY
===================================================== */

.edit-job-overlay {
  position: fixed !important;
  inset: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 1000000 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(27, 14, 38, .64);
  backdrop-filter: blur(5px);
  animation: overlayIn .2s ease;
}

@keyframes overlayIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* =====================================================
   EDIT MODAL
===================================================== */

.edit-job-modal {
  position: relative;
  width: min(900px, 96vw);
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow:
    0 30px 100px rgba(30, 10, 50, .38);
  animation: modalIn .25s ease;
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: translateY(15px) scale(.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* =====================================================
   EDIT HEADER
===================================================== */

.edit-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 17px 21px;
  background: #3E0F8D;
  color: #fff;
}

.edit-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.edit-header-icon {
  width: 39px;
  height: 39px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: rgba(255,255,255,.13);
}

.edit-modal-header h2 {
  margin: 0;
  font-size: 18px;
}

.edit-modal-header p {
  margin: 3px 0 0;
  opacity: .75;
  font-size: 10px;
}

/* =====================================================
   EDIT BODY
===================================================== */

.edit-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 21px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  color: #46394f;
  font-size: 11px;
  font-weight: 750;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 11px;
  border: 1px solid #dcd5e3;
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: #30263a;
  font-size: 12px;
  transition: .2s ease;
}

.form-group input {
  height: 38px;
}

.form-group textarea {
  min-height: 110px;
  resize: vertical;
  line-height: 1.6;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #9564DD;
  box-shadow:
    0 0 0 3px rgba(149,100,221,.11);
}

.form-group small {
  display: block;
  margin-top: 5px;
  color: #998ca2;
  font-size: 9px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 14px;
}

/* =====================================================
   READ ONLY
===================================================== */

.readonly-heading {
  margin: 18px 0 9px;
  color: #3E0F8D;
  font-size: 12px;
  font-weight: 750;
}

.readonly-info {
  display: grid;
  grid-template-columns:
    repeat(4, 1fr);
  gap: 9px;
  padding: 12px;
  border-radius: 10px;
  background: #f7f5f9;
  border: 1px solid #ece7f0;
}

.readonly-info div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.readonly-info span {
  color: #978b9f;
  font-size: 9px;
}

.readonly-info strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #493d51;
  font-size: 11px;
}

/* =====================================================
   ANIMATION
===================================================== */

.refresh-spin {
  animation:
    refreshSpin 1s linear infinite;
}

@keyframes refreshSpin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* =====================================================
   RESPONSIVE
===================================================== */

@media (max-width: 1100px) {
  .filter-grid {
    grid-template-columns:
      repeat(4, 1fr);
  }
}

@media (max-width: 768px) {

  .my-jobs-page {
    padding: 14px;
  }

  .my-jobs-card {
    padding: 14px;
  }

  .jobs-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .refresh-btn,
  .filter-toggle-btn {
    flex: 1;
  }

  .filter-grid {
    grid-template-columns:
      repeat(2, 1fr);
  }

  .preview-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .readonly-info {
    grid-template-columns:
      repeat(2, 1fr);
  }

  .job-preview-drawer {
    width: 100vw;
  }

  .edit-job-modal {
    width: 96vw;
    max-height: 95vh;
  }
}

@media (max-width: 480px) {

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .readonly-info {
    grid-template-columns: 1fr;
  }

  .edit-job-overlay {
    padding: 8px;
  }

  .edit-modal-body {
    padding: 15px;
  }

  .preview-body {
    padding: 15px;
  }

  .preview-footer,
  .edit-modal-footer {
    padding: 12px;
  }

}

`;

export default MyJobs;